import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Zap, Download, ShieldCheck } from "lucide-react";
import api from "../lib/apiClient";
import { ProductCard } from "../components/ProductCard";

const CATEGORIES = ["All", "AI Packs", "E-books", "Business", "Bundles"];

export default function Storefront() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "All";

  useEffect(() => {
    setLoading(true);
    api
      .get("/products", { params: activeCategory !== "All" ? { category: activeCategory } : {} })
      .then((res) => setProducts(res.data))
      .finally(() => setLoading(false));
  }, [activeCategory]);

  const featured = products.find((p) => p.featured) || products[0];

  return (
    <div data-testid="storefront-page">
      {/* Hero */}
      <section className="border-b border-obsidian/15">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 md:grid-cols-2 md:items-center md:px-8 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-signal">
              Digital goods · Instant delivery
            </span>
            <h1 className="mt-5 font-serif text-5xl font-light leading-none tracking-tighter text-obsidian md:text-6xl lg:text-7xl">
              Build once.<br />Sell forever.
            </h1>
            <p className="mt-6 max-w-md text-base leading-relaxed text-obsidian/70">
              A curated shop of premium templates, kits, and playbooks that help you launch a
              profitable product — with almost nothing to start.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="#catalog"
                data-testid="hero-shop-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-signal px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] text-white transition-colors hover:bg-red-700"
              >
                Browse the shop
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-obsidian/60">
                <ShieldCheck className="h-4 w-4 text-signal" /> Lifetime access
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="relative"
          >
            <div className="aspect-[4/5] overflow-hidden border border-obsidian/15">
              <img
                src="https://images.unsplash.com/photo-1622737133809-d95047b9e673"
                alt="Digital art"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden w-48 border border-obsidian/15 bg-white p-4 shadow-[6px_6px_0px_#0A0A0A] md:block">
              <div className="flex items-center gap-2 text-signal">
                <Zap className="h-4 w-4" />
                <span className="text-xs font-semibold uppercase tracking-[0.15em]">Fastest sellers</span>
              </div>
              <p className="mt-2 font-serif text-3xl tracking-tighter text-obsidian">$10k+</p>
              <p className="text-xs text-obsidian/50">earned by creators this month</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Feature strip */}
      <section className="border-b border-obsidian/15 bg-obsidian text-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-white/10 md:grid-cols-3 md:divide-x md:divide-y-0">
          {[
            { icon: Download, title: "Instant download", desc: "Files delivered the moment you check out." },
            { icon: Zap, title: "Zero overhead", desc: "No inventory, no shipping — pure margin." },
            { icon: ShieldCheck, title: "Lifetime updates", desc: "Buy once, get every future revision free." },
          ].map((f) => (
            <div key={f.title} className="flex items-start gap-4 p-8">
              <f.icon className="h-6 w-6 shrink-0 text-signal" />
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-[0.15em]">{f.title}</h3>
                <p className="mt-1 text-sm text-white/55">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Catalog */}
      <section id="catalog" className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-obsidian/50">
              The Collection
            </span>
            <h2 className="mt-3 font-serif text-3xl font-light tracking-tight text-obsidian md:text-4xl">
              Everything you need to launch
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                data-testid={`filter-${cat}`}
                onClick={() => setSearchParams(cat === "All" ? {} : { category: cat })}
                className={`border px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-colors ${
                  activeCategory === cat
                    ? "border-obsidian bg-obsidian text-white"
                    : "border-obsidian/20 text-obsidian/70 hover:border-obsidian"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="aspect-[4/3] animate-pulse bg-secondary" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <p className="mt-12 text-obsidian/50">No products in this category yet.</p>
        ) : (
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-12">
            {products.map((product, i) => {
              const isBig = i % 5 === 0;
              return (
                <div key={product.id} className={isBig ? "md:col-span-8" : "md:col-span-4"}>
                  <ProductCard product={product} index={i} large={isBig} />
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
